import * as XLSX from "xlsx";
import { findMatchingHeader, normalizeHeader } from "./posRecoColumnSchema";

const toHeaderList = (row = []) =>
	(row || [])
		.map((cell) => String(cell ?? "").trim())
		.filter((cell) => cell && !["nan", "null", "undefined"].includes(cell.toLowerCase()));

const getSheet = (workbook, sheetName) => {
	if (sheetName && workbook.Sheets[sheetName]) return workbook.Sheets[sheetName];
	const fallback = workbook.SheetNames[0];
	return fallback ? workbook.Sheets[fallback] : null;
};

const sheetToRows = (sheet, maxRows = 80) =>
	XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false, raw: false }).slice(0, maxRows);

const rowContainsHeaders = (row, requiredHeaders = []) => {
	const normalizedRow = new Set(toHeaderList(row).map((header) => normalizeHeader(header)));
	return requiredHeaders
		.filter(Boolean)
		.every((header) => normalizedRow.has(normalizeHeader(header)));
};

export const readPosRecoFileHeaders = (file, mopConfig = {}, mappingColumns = []) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (event) => {
			try {
				const data = new Uint8Array(event.target.result);
				const workbook = XLSX.read(data, { type: "array", sheetRows: 120 });
				const sheet = getSheet(workbook, mopConfig.sheetName);
				if (!sheet) {
					resolve([]);
					return;
				}

				const rows = sheetToRows(sheet, 120);
				const headerMode = mopConfig.headerMode || "first_row";

				if (headerMode === "row_index") {
					const headerRow = Number.isInteger(mopConfig.headerRow) ? mopConfig.headerRow : 0;
					resolve(toHeaderList(rows[headerRow] || []));
					return;
				}

				if (headerMode === "after_marker") {
					const marker = String(mopConfig.marker || "").toLowerCase();
					const markerIndex = rows.findIndex((row) =>
						(row || []).some((cell) => String(cell).toLowerCase().includes(marker)),
					);
					if (markerIndex >= 0) {
						resolve(toHeaderList(rows[markerIndex + 1] || []));
						return;
					}
					resolve(toHeaderList(rows[0] || []));
					return;
				}

				if (headerMode === "match_mapped") {
					const requiredHeaders = (mappingColumns || [])
						.filter((column) => column.is_required && column.file_header)
						.map((column) => column.file_header);
					const matchIndex = rows.findIndex((row) => rowContainsHeaders(row, requiredHeaders));
					if (matchIndex >= 0) {
						resolve(toHeaderList(rows[matchIndex] || []));
						return;
					}
					const firstNonEmpty = rows.find((row) => toHeaderList(row).length > 0);
					resolve(toHeaderList(firstNonEmpty || []));
					return;
				}

				resolve(toHeaderList(rows[0] || []));
			} catch (err) {
				reject(err);
			}
		};
		reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
		reader.readAsArrayBuffer(file);
	});

export const autoMatchColumnMap = (columns = [], fileHeaders = []) => {
	const next = {};
	(columns || []).forEach((column) => {
		const matched =
			findMatchingHeader(fileHeaders, column.file_header) ||
			findMatchingHeader(fileHeaders, column.internal_column) ||
			"";
		next[column.internal_column] = matched;
	});
	return next;
};
